import { RenderedDie } from "@/components/RenderedDie";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import type { DieType, Manifest } from "@/types";
import { Suspense, useMemo } from "react";
import { InstancedRenderedDiceBatch } from "./components/InstancedRenderedDiceBatch";
import { getInstancingKey } from "./helpers/getInstancingKey";

type InstancedBatch = {
  dieType: DieType;
  ids: string[];
  key: string;
  setId: string;
};

const getRenderedDiceGroups = (
  ids: string[],
  manifests: Record<string, Manifest>,
) => {
  const state = useRenderedDiceStore.getState();
  const instancedBatches = new Map<string, InstancedBatch>();
  const legacyIds: string[] = [];

  ids.forEach((id) => {
    const die = state.renderedDice.entities[id];
    const roll = state.rolls.entities[die?.rollId];
    const setId = roll?.message.data.setId ?? die?.setId;
    const manifest = setId ? manifests[setId] : undefined;
    const instancingKey =
      die && setId
        ? getInstancingKey({
            dieType: die.dieType,
            manifest,
            setId,
          })
        : undefined;

    if (!die || !setId || !instancingKey) {
      legacyIds.push(id);
      return;
    }

    const batch = instancedBatches.get(instancingKey);

    if (batch) {
      batch.ids.push(id);
      return;
    }

    instancedBatches.set(instancingKey, {
      dieType: die.dieType,
      ids: [id],
      key: instancingKey,
      setId,
    });
  });

  return {
    instancedBatches: Array.from(instancedBatches.values()),
    legacyIds,
  };
};

export const RenderedDice = () => {
  const ids = useRenderedDiceStore((state) => state.renderedDice.ids);
  const manifests = useRenderedDiceStore((state) => state.manifests.entities);
  const { instancedBatches, legacyIds } = useMemo(
    () => getRenderedDiceGroups(ids, manifests),
    [ids, manifests],
  );

  return (
    <>
      {instancedBatches.map((batch) => (
        <Suspense key={batch.key}>
          <InstancedRenderedDiceBatch
            dieType={batch.dieType}
            ids={batch.ids}
            setId={batch.setId}
          />
        </Suspense>
      ))}
      {legacyIds.map((id: string) => (
        <Suspense key={`${id}-die`}>
          <RenderedDie id={id} />
        </Suspense>
      ))}
    </>
  );
};
